import { Link } from 'react-router-dom';
import footerImage from '../../../assets/images/footer.png';
import logo from '../../../assets/images/logo.png';
import {
  FOOTER_HELP_LINKS,
  FOOTER_INFO_LINKS,
} from '../../../constants/homeData';
import { ROUTES } from '../../../constants/routes';
import { useContactInformation } from '../../../hooks/useContactInformation';
import apartment from '../../../assets/images/apartment.svg';
import mobile from '../../../assets/images/mobile.svg';
import mail from '../../../assets/images/mail.svg';

const FALLBACK_CONTACT = {
  address: 'Vivekananda Marg, Chidiapolo, Balasore Odisha-756001',
  phone: '9853747487',
  email: 'info@chseexpress.com',
};

function ContactRow({ icon, children, href }) {
  const inner = href ? (
    <a href={href} className="text-[#5E7BB8] text-base">
      {children}
    </a>
  ) : (
    <span className="text-[#5E7BB8] text-base">{children}</span>
  );

  return (
    <li className="flex gap-3 text-sm">
      <span className="shrink-0 text-sky-400">{icon}</span>
      <span className="leading-relaxed">{inner}</span>
    </li>
  );
}

function FooterNavLink({ link }) {
  const className = 'text-base font-medium text-[#5E7BB8] transition hover:text-white';

  if (link.policyType) {
    return (
      <Link to={ROUTES.POLICY(link.policyType)} className={className}>
        {link.label}
      </Link>
    );
  }

  if (link.isRoute && link.path) {
    return (
      <Link to={link.path} className={className}>
        {link.label}
      </Link>
    );
  }

  return (
    <a href={link.href || '#'} className={className}>
      {link.label}
    </a>
  );
}

function SocialLink({ href, label, children }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center justify-center text-[#DCDDDF] transition hover:text-white"
      aria-label={label}
    >
      {children}
    </a>
  );
}

function YouTubeIcon() {
  return (
    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden>
      <rect x="2.25" y="5.5" width="19.5" height="13" rx="3.5" />
      <path strokeLinejoin="round" d="M10 9.75v4.5L14.5 12 10 9.75z" />
    </svg>
  );
}

function FacebookIcon() {
  return (
    <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="3" y="3" width="18" height="18" rx="4" stroke="currentColor" strokeWidth={1.5} />
      <path
        fill="currentColor"
        d="M13.4 18.75v-6.1h2.05l.31-2.35H13.4V8.85c0-.68.19-1.15 1.18-1.15h1.26V5.58c-.22-.03-.97-.09-1.84-.09-1.82 0-3.07 1.11-3.07 3.15v1.76H9.1v2.35h1.83v6.1h2.47z"
      />
    </svg>
  );
}

function InstagramIcon() {
  return (
    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden>
      <rect x="3" y="3" width="18" height="18" rx="5" />
      <circle cx="12" cy="12" r="3.75" />
      <circle cx="17.25" cy="6.75" r="0.9" fill="currentColor" stroke="none" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="3" y="3" width="18" height="18" rx="4" stroke="currentColor" strokeWidth={1.5} />
      <path
        fill="currentColor"
        transform="translate(6.5 6.5) scale(0.46)"
        d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"
      />
    </svg>
  );
}

function FooterSection() {
  const { contact: apiContact, loading } = useContactInformation();
  const contact = apiContact || FALLBACK_CONTACT;
  const address = contact.address?.trim();
  const phone = contact.phone?.trim();
  const email = contact.email?.trim();

  const socialLinks = [
    { label: 'YouTube', href: apiContact?.youtube_url, Icon: YouTubeIcon },
    { label: 'Facebook', href: apiContact?.facebook_url, Icon: FacebookIcon },
    { label: 'Instagram', href: apiContact?.instagram_url, Icon: InstagramIcon },
    { label: 'X (Twitter)', href: apiContact?.twitter_url, Icon: XIcon },
  ].filter((link) => link.href);

  return (
    <footer className="relative overflow-hidden text-white mt-10">
      {/* Top Black Area */}
      <div className="h-[200px] md:h-[60px]" />

      {/* Footer Background Image */}
      <div className="absolute bottom-0 left-0 w-full">
        <img
          src={footerImage}
          alt="footer background"
          className="w-full object-cover"
        />
      </div>

      {/* Content */}
      <div className="relative z-20 mx-auto max-w-7xl px-6 sm:px-8 lg:px-10">
        <div className="grid gap-10 lg:grid-cols-[1.15fr_0.85fr_0.85fr_0.6fr] lg:gap-8 mb-6">
          
          {/* Logo + Contact */}
          <div>
            <img
              src={logo}
              alt="CHSE express"
              className="h-[104px] w-[175px]  "
            />

            <p className="mt-5 max-w-xs text-base leading-relaxed text-[#EAECF0]">
              Top learning experiences that create more talent in the world.
            </p>

            <ul className="mt-12 space-y-4">
              {loading ? (
                <>
                  <li className="h-5 w-60 animate-pulse rounded bg-white/10" />
                  <li className="h-5 w-32 animate-pulse rounded bg-white/10" />
                  <li className="h-5 w-48 animate-pulse rounded bg-white/10" />
                </>
              ) : (
                <>
                  {address ? (
                    <ContactRow icon={<img src={apartment} alt="address" className="h-5 w-5" />}>
                      {address}
                    </ContactRow>
                  ) : null}

                  {phone ? (
                    <ContactRow
                      href={`tel:${phone.replace(/\s/g, '')}`}
                      icon={<img src={mobile} alt="phone" className="h-5 w-5" />}
                    >
                      {phone}
                    </ContactRow>
                  ) : null}

                  {email ? (
                    <ContactRow
                      href={`mailto:${email}`}
                      icon={<img src={mail} alt="mail" className="h-5 w-5" />}
                    >
                      {email}
                    </ContactRow>
                  ) : null}
                </>
              )}
            </ul>
          </div>

          {/* Help Center */}
          <div className='mt-32'>
            <h3 className="text-base font-semibold text-[#F9F9F9]">Help Center</h3>

            <ul className="mt-5 space-y-3">
              {FOOTER_HELP_LINKS.map((link) => (
                <li key={link.label}>
                  <FooterNavLink link={link} />
                </li>
              ))}
            </ul>
          </div>

          {/* Information */}
          <div className='mt-32'>
            <h3 className="text-base font-semibold text-[#F9F9F9]">Information</h3>
            <ul className="mt-5 space-y-3">
              {FOOTER_INFO_LINKS.map((link) => (
                <li key={link.label}>
                  <FooterNavLink link={link} />
                </li>
              ))}
            </ul>
          </div>

          {/* Empty Right Space */}
          <div className="hidden lg:block" />
        </div>

        {/* Social Icons */}
        <div className="mb-32 flex min-h-6 flex-wrap items-center justify-center gap-5">
          {socialLinks.length > 0 ? (
            <>
              <span className="text-[20px] font-normal lowercase tracking-wide text-[#DCDDDF]">
                find us
              </span>

              <div className="flex items-center gap-5">
                {socialLinks.map(({ label, href, Icon }) => (
                  <SocialLink key={label} label={label} href={href}>
                    <Icon />
                  </SocialLink>
                ))}
              </div>
            </>
          ) : null}
        </div>
      </div>
    </footer>
  );
}

export default FooterSection;
